import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, List, Plus, RefreshCw } from 'lucide-react';
import { listConferimenti, deleteConferimento } from '../api/conferimenti';
import type { ConferimentoListItem } from '../api/conferimenti';
import { getCapacitaCalendario } from '../api/programmazione';
import type { GiornoCalendario } from '../api/programmazione';
import { getChiusure } from '../api/chiusure';
import type { Chiusura } from '../api/chiusure';
import ConferimentiListaView from '../components/conferimenti/ConferimentiListaView';
import ConferimentiCalendarioView from '../components/conferimenti/ConferimentiCalendarioView';
import ProgrammaConferimentoModal from '../components/modals/ProgrammaConferimentoModal';
import GestioneGiornoModal from '../components/programmazione/GestioneGiornoModal';
import { useAuthStore } from '../store/authStore';
import { canWriteProgrammazione } from '../lib/constants';
type ViewMode = 'list' | 'calendar';
type SortKey = 'data_prevista' | 'numero' | 'status';

const STATUS_OPTIONS = [
  { value: '', labelKey: 'programmazione.tutti_stati' },
  { value: 'programmato', labelKey: 'conferimenti.card.stato.programmato' },
  { value: 'in_ritardo', labelKey: 'conferimenti.card.stato.in_ritardo' },
  { value: 'in_transito', labelKey: 'conferimenti.card.stato.in_transito' },
  { value: 'attesa_compliance', labelKey: 'conferimenti.card.stato.attesa_compliance' },
  { value: 'in_stoccaggio', labelKey: 'conferimenti.card.stato.in_stoccaggio' },
  { value: 'respinto', labelKey: 'conferimenti.card.stato.respinto' },
];

export default function ProgrammazionePage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = (searchParams.get('view') ?? 'list') as ViewMode;
  const [items, setItems] = useState<ConferimentoListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [dataDa, setDataDa] = useState(searchParams.get('data_da') ?? '');
  const [dataA, setDataA] = useState(searchParams.get('data_a') ?? '');
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<SortKey>('data_prevista');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showProgramma, setShowProgramma] = useState(false);
  const [programmaInitialDate, setProgrammaInitialDate] = useState<string | undefined>(undefined);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [saturazioni, setSaturazioni] = useState<Record<string, GiornoCalendario>>({});
  const [chiusureMap, setChiusureMap] = useState<Record<string, Chiusura>>({});

  const canWrite = canWriteProgrammazione(user?.role);

  const loadSaturazioni = useCallback((date: Date) => {
    void (async () => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const to = `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`;
    try {
      const [satRes, chiusureRes] = await Promise.all([
        getCapacitaCalendario(from, to),
        getChiusure(from, to),
      ]);
      const satMap: Record<string, GiornoCalendario> = {};
      for (const g of satRes.giorni) { satMap[g.data] = g; }
      setSaturazioni(satMap);
      const cMap: Record<string, Chiusura> = {};
      for (const c of chiusureRes.chiusure) { cMap[c.data] = c; }
      setChiusureMap(cMap);
    } catch {
      // silently fail
    }
    })();
  }, []);

  function setView(v: ViewMode) {
    setSearchParams((p) => { p.set('view', v); return p; });
  }

  const fetchData = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, unknown> = { limit: 100, offset };
        if (statusFilter) params.status = statusFilter;
        if (dataDa) params.data_da = dataDa;
        if (dataA) params.data_a = dataA;
        const res = await listConferimenti(params as Parameters<typeof listConferimenti>[0]);
        const sorted = [...res.items];
        sorted.sort((a, b) => {
          let va = a[sortBy] ?? '';
          let vb = b[sortBy] ?? '';
          if (typeof va === 'string' && typeof vb === 'string') {
            va = va.toLowerCase(); vb = vb.toLowerCase();
          }
          return sortDir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
        });
        setItems(sorted);
        setTotal(res.total);
      } catch {
        setError(t('programmazione.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [statusFilter, dataDa, dataA, offset, sortBy, sortDir, t]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (view === 'calendar') loadSaturazioni(calendarDate); }, [view, calendarDate, loadSaturazioni]);

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm(t('programmazione.conferma_elimina'))) return;
    try {
      await deleteConferimento(id);
      fetchData();
    } catch {
      alert(t('programmazione.errore_elimina'));
    }
  }

  const inputCls = 'text-sm border border-stone-border rounded px-2 py-1.5 text-ink-dark focus:outline-none focus:border-sage bg-white';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-ink-dark">{t('programmazione.titolo')}</h1>
          <p className="text-sm text-ink-soft mt-0.5">{t('programmazione.conteggio', { total })}</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchData} className="p-2 text-ink-soft hover:text-ink-dark border border-stone-border rounded-lg bg-white" title={t('programmazione.aggiorna')}>
            <RefreshCw size={15} />
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowProgramma(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors"
            >
              <Plus size={15} /> {t('programmazione.programma')}
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-white border border-stone-mid rounded-xl">
        {/* Vista toggle */}
        <div className="flex items-center border border-stone-border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setView('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${view === 'list' ? 'bg-sage text-white' : 'text-ink-mid hover:bg-stone-bg'}`}
          >
            <List size={14} /> {t('programmazione.lista')}
          </button>
          <button
            type="button"
            onClick={() => setView('calendar')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${view === 'calendar' ? 'bg-sage text-white' : 'text-ink-mid hover:bg-stone-bg'}`}
          >
            <Calendar size={14} /> {t('programmazione.calendario')}
          </button>
        </div>

        {/* Filtri */}
        <select className={inputCls} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <label className="text-xs text-ink-soft">{t('programmazione.dal')}</label>
          <input type="date" className={inputCls} value={dataDa} onChange={(e) => { setDataDa(e.target.value); setOffset(0); }} />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-xs text-ink-soft">{t('programmazione.al')}</label>
          <input type="date" className={inputCls} value={dataA} onChange={(e) => { setDataA(e.target.value); setOffset(0); }} />
        </div>
        {(statusFilter || dataDa || dataA) && (
          <button
            type="button"
            onClick={() => { setStatusFilter(''); setDataDa(''); setDataA(''); setOffset(0); }}
            className="text-xs text-ink-soft hover:text-accent-danger"
          >
            {t('programmazione.azzera_filtri')}
          </button>
        )}
      </div>

      {/* Contenuto */}
      {error && (
        <div className="p-3 mb-4 bg-accent-danger-bg border border-[#FECACA] rounded-lg text-sm text-accent-danger">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-ink-soft text-sm">{t('programmazione.caricamento')}</div>
      ) : view === 'list' ? (
        <ConferimentiListaView
          items={items}
          total={total}
          limit={100}
          offset={offset}
          onPageChange={setOffset}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          onDelete={canWrite ? handleDelete : undefined}
        />
      ) : (
        <ConferimentiCalendarioView
          items={items}
          saturazioni={saturazioni}
          onSelectDay={setSelectedDay}
          onNavigate={(date) => setCalendarDate(date)}
        />
      )}

      {/* Modale programma */}
      {showProgramma && (
        <ProgrammaConferimentoModal
          initialDate={programmaInitialDate}
          onClose={() => { setShowProgramma(false); setProgrammaInitialDate(undefined); }}
          onSaved={() => { setShowProgramma(false); setProgrammaInitialDate(undefined); fetchData(); }}
        />
      )}

      {/* Gestione giorno */}
      {selectedDay && (
        <GestioneGiornoModal
          data={selectedDay}
          chiusuraEsistente={chiusureMap[selectedDay] ?? null}
          onClose={() => setSelectedDay(null)}
          onChiusuraUpdated={() => { loadSaturazioni(calendarDate); fetchData(); }}
          onProgrammaNuovo={(data) => {
            setSelectedDay(null);
            setProgrammaInitialDate(data);
            setShowProgramma(true);
          }}
        />
      )}
    </div>
  );
}
